<template>
  <TemplateHead :config="config" />

  <div :class="fonts[config.style.font]">
    <div class="w-full flex justify-center p-2">
      <img
        v-if="config.header?.logo_url"
        class="h-[4rem] object-contain"
        :src="config.header?.logo_url"
        alt="Logo"
      >
      <img
        v-else-if="isPreview()"
        class="h-[4rem] object-contain"
        src="https://placehold.co/190x60"
        alt="Logo"
      >
    </div>

    <div class="w-full relative md:h-[46rem]">
      <div
        class="absolute h-1/2 w-full z-[-1]"
        :style="{
          backgroundImage: `linear-gradient(to right, #${primaryColor}, #${secondaryColor}, #${primaryColor})`
        }"
      />
      <div
        class="absolute h-full w-full z-[-2]"
        :style="{
          backgroundColor: `#${ config.style?.body}`,
        }"
      />

      <div class="flex justify-center z-10 p-4 h-full">
        <div class="grid grid-cols-1 md:grid-cols-2 md:grid-rows-2 w-[64rem]">
          <div class="grid flex items-center justify-center px-2">
            <p class="text-4xl font-semibold text-center text-white">
              {{ config.body?.title ?? __('pre_lander_001_header_title') }}
            </p>

            <p class="text-white text-xl text-center">
              {{ config.body?.subtitle ?? __('pre_lander_001_header_subtitle') }}
            </p>
          </div>

          <div class="md:row-span-2 md:grid md:grid-rows-4">
            <div class="md:row-span-3 py-8">
              <div class="bg-white rounded-md shadow-md h-full">
                <div class="p-2 h-full">
                  <div>
                    <div class="flex justify-center">
                      <p class="text-gray-600 text-xs">
                        {{ __('label_progress') }}
                      </p>
                    </div>

                    <div
                      class="px-2 w-full grid gap-0.5"
                      :style="{
                        gridTemplateColumns: `repeat(${stepCount}, minmax(0, 1fr))`,
                      }"
                    >
                      <div
                        v-for="index in [...Array(stepCount).keys()]"
                        :key="index"
                      >
                        <div
                          class="w-full p-2"
                          :style="{
                            backgroundColor: activeStepIndex >= index ? '#' + progressBarColor : '#e5e5e5'
                          }"
                        />
                      </div>
                    </div>

                    <div v-if="activeStepIndex !== (stepCount - 1)">
                      <div class="flex justify-center w-full pt-8">
                        <img
                          v-if="activeStep?.image_url"
                          class="w-48 h-32 object-contain"
                          :src="activeStep?.image_url"
                          alt="Image"
                        >
                      </div>

                      <div>
                        <p class="text-3xl text-center p-4">
                          {{ activeStep?.title }}
                        </p>
                      </div>

                      <div
                        class="w-full grid p-2 gap-4"
                        :style="{
                          gridTemplateColumns: isMobile ? 'repeat(1, minmax(0, 1fr))' : `repeat(${activeStep?.options?.length}, minmax(0, 1fr))`
                        }"
                      >
                        <div
                          v-for="(option, index) in activeStep?.options"
                          :key="index"
                          class="text-center text-white text-2xl font-semibold rounded-xl shadow-xl h-32 flex items-center justify-center cursor-pointer hover:shadow-sm break-words"
                          :style="{
                            backgroundImage: `linear-gradient(to right, #${buttonPrimaryColor}, #${buttonSecondaryColor})`
                          }"
                          @click="answerQuestion(option)"
                        >
                          {{ option.title }}
                        </div>
                      </div>

                      <div
                        v-if="quizConfig?.show_navigation !== '0'"
                        class="w-full flex justify-center p-2"
                      >
                        <div class="grid grid-cols-2 w-1/2 text-center gap-6">
                          <div
                            class="rounded-md p-1 text-white hover:bg-gray-300"
                            :class="{
                              'cursor-not-allowed bg-gray-300': !canGoPreviousStep,
                              'bg-gray-400 cursor-pointer': canGoPreviousStep,
                            }"
                            @click="previousStep"
                          >
                            <i class="far fa-chevron-left text-sm" /> {{ __('label_back') }}
                          </div>

                          <div
                            class="rounded-md p-1 text-white hover:bg-gray-300"
                            :class="{
                              'cursor-not-allowed bg-gray-300': !canGoNextStep,
                              'bg-gray-400 cursor-pointer': canGoNextStep,
                            }"
                            @click="nextStep"
                          >
                            {{ __('label_next') }} <i class="far fa-chevron-right text-sm" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else>
                      <div class="flex justify-center w-full pt-8">
                        <img
                          v-if="customOutImageUrl"
                          class="w-[11rem] h-[11rem] object-contain"
                          :src="customOutImageUrl"
                          alt="Image"
                        >
                        <img
                          v-else
                          class="w-[11rem] h-[11rem] object-contain"
                          src="@timon/js/Templates/PRE-LANDER-001/assets/party.svg"
                          alt="Image"
                        >
                      </div>

                      <div class="text-center p-4">
                        <p
                          class="text-2xl font-bold mb-1"
                          :style="{ color: `#${primaryColor}` }"
                        >
                          {{ outTitle }}
                        </p>

                        <p>
                          {{ outText }}
                        </p>
                      </div>

                      <div class="flex justify-center">
                        <a
                          class="text-center text-white text-2xl font-semibold rounded-xl shadow-xl flex items-center justify-center cursor-pointer py-4 px-8 hover:shadow-sm"
                          :style="{
                            backgroundImage: `linear-gradient(to right, #${buttonPrimaryColor}, #${buttonSecondaryColor})`
                          }"
                          :href="outUrl"
                        >
                          {{ outButtonText }}
                        </a>
                      </div>

                      <p class="text-sm text-gray-500 text-center p-2 italic">
                        {{ __('pre_lander_001_out_subtext') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="text-center p-8">
              <div
                class="text-2xl font-bold"
                :style="{ color: `#${primaryColor}` }"
              >
                {{ __('pre_lander_001_body_title') }}
              </div>

              <div class="text-lg">
                {{ __('pre_lander_001_body_subtitle') }}
              </div>
            </div>
          </div>

          <div class="flex justify-center">
            <img
              v-if="config.body?.image_url"
              class="h-64 w-64 object-contain m-8"
              :src="config.body?.image_url"
              alt="Image"
            >
            <img
              v-else
              class="h-64 w-64 object-contain m-8"
              src="@timon/js/Templates/PRE-LANDER-001/assets/presents.svg"
              alt="Image"
            >
          </div>
        </div>
      </div>
    </div>

    <ExtendedBody
      v-if="(!!config?.extended_body?.enabled) && (config?.extended_body?.enabled !== '0')"
      class="col-span-2"
      :config="config"
    />

    <Footer
      :config="config"
      :pages="pages"
      :collector-flow-uuid="collectorFlowUuid"
    />
  </div>
</template>

<script setup>
import {computed, defineProps, onMounted, onUnmounted, ref} from 'vue';
import {
  __,
  checkIfTargetingCookiesAreAccepted,
  clickoutParameters,
  isPreview,
  registerVisit,
} from '@timon/js/Mixins/helpers';
import Footer from '@timon/js/Templates/PRE-LANDER-001/Components/Footer.vue';
import TemplateHead from '@timon/js/Components/TemplateHead.vue';
import ExtendedBody from '@timon/js/Templates/PRE-LANDER-001/Components/ExtendedBody.vue';

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },

  quizConfig: {
    type: Object,
    required: true,
  },

  variables: {
    type: Object,
    required: true,
  },

  collectorFlowUuid: {
    type: String,
    required: true,
  },

  pages: {
    type: Object,
    required: true,
  },

  adUrlUuid: {
    type: String,
    required: false,
    default: null,
  },
});

const fonts = {
  'mono': 'font-mono',
  'sans': 'font-sans',
  'serif': 'font-serif',
};

const stepCount = ref((props.quizConfig?.steps?.length ?? 0) + 1);
const activeStepIndex = ref(0);

const outTitle = ref(props.quizConfig?.out_title ?? __('pre_lander_001_out_title'));
const outText = ref(props.quizConfig?.out_text ?? __('pre_lander_001_out_text'));
const outUrl = ref('#');
const customOutImageUrl = ref(props.quizConfig?.custom_out_image_url ?? null);
const outButtonText = ref(__('pre_lander_001_out_button_text'));

const primaryColor = ref(props.config.style?.primary);
const secondaryColor = ref(props.config.style?.secondary);

const buttonPrimaryColor = ref(props.config.style?.button_primary);
const buttonSecondaryColor = ref(props.config.style?.button_secondary);

const progressBarColor = ref(props.config.style?.progress_bar);

const canGoPreviousStep = computed(() => activeStepIndex.value > 0 && activeStepIndex.value !== (stepCount.value - 1));
const canGoNextStep = computed(() => activeStepIndex.value < stepCount.value - 1);

const isMobile = ref(false);

const landingVisitUuid = ref(null);

function updateIsMobile() {
  isMobile.value = window.innerWidth < 768;
}
function acceptedCookies() {
  checkIfTargetingCookiesAreAccepted(() => registerVisit(props.adUrlUuid));
}

onMounted(() => {
  updateIsMobile();

  window.addEventListener('resize', updateIsMobile);
  window.addEventListener('CookieScriptAcceptAll', acceptedCookies);
  window.addEventListener('CookieScriptAccept', acceptedCookies);

  if (!isPreview()) {
    registerVisit(props.adUrlUuid, (data) => {
      landingVisitUuid.value = data.visit_key;

      outUrl.value = route('zazu.v1.clickout', {
        uuid: landingVisitUuid.value,
        ...clickoutParameters(),
      });
    });
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobile);
  window.removeEventListener('CookieScriptAcceptAll', acceptedCookies);
  window.removeEventListener('CookieScriptAccept', acceptedCookies);
});

function nextStep() {
  if (!canGoNextStep.value) {
    return;
  }

  activeStepIndex.value += 1;
}

function previousStep() {
  if (!canGoPreviousStep.value) {
    return;
  }

  activeStepIndex.value -= 1;
}

function answerQuestion(answer) {
  if (!answer.links_out || answer.links_out === '0') {
    nextStep();
    return;
  }

  outTitle.value = answer.out_title;
  outText.value = answer.out_text;
  outButtonText.value = answer.out_button_text;
  if (!isPreview()) {
    outUrl.value = route('zazu.v1.clickout', {
      uuid: landingVisitUuid.value,
      adUrl: answer.ad_url_uuid,
      ...clickoutParameters(),
    });
  }

  if (answer.out_image_url) {
    customOutImageUrl.value = answer.out_image_url;
  }

  activeStepIndex.value = stepCount.value - 1;
}

const activeStep = computed(() => props.quizConfig?.steps[activeStepIndex.value] ?? null);
</script>
